import { Component, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { SwUpdate, VersionEvent, VersionReadyEvent } from '@angular/service-worker';
import { HotkeysService, truthy } from '@nestech/utils';
import { TranslateService } from '@ngx-translate/core';
import { Toast, InterpolatedTranslatePipe } from '@web/common';
import { SystemVersionService, Toaster } from '@web/service';
import { filter, first, Subject, switchMap, timeout } from 'rxjs';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'cassiopeia-root',
  template: `
    <router-outlet></router-outlet>
    <cas-toast></cas-toast>
    <!-- <web-translate-widget/> -->
    <ng-template #inactivityBody>
      {{ 'cas.inactivityBody' | translate: { notificationThresholdTime, timeUntilLogout } }}
    </ng-template>
  `,
  imports: [RouterOutlet, Toast, InterpolatedTranslatePipe],
})
export class CassiopeiaRootComponent implements OnInit {
  private updates = inject(SwUpdate);
  private toaster = inject(Toaster);
  private translate = inject(TranslateService);
  private hotkeysService = inject(HotkeysService);
  private systemVersionService = inject(SystemVersionService);

  notificationThresholdTime?: string;
  timeUntilLogout?: string;

  ngOnInit(): void {
    this.onUpdateActivated(() => location.reload());
    this.addVersionHotkey();
  }

  private addVersionHotkey() {
    this.hotkeysService
      .add('alt.shift.v')
      .pipe(truthy())
      .subscribe(() => {
        const version = this.systemVersionService.version().version;
        this.toaster.info(this.translate.instant('cas.version', { version }));
      });
  }

  private onUpdateActivated(updateActivatedFn: () => void): void {
    this.updates.versionUpdates
      .pipe(
        filter((event: VersionEvent): event is VersionReadyEvent => event.type === 'VERSION_READY'),
        switchMap(event => {
          const getAppData = (aVersion: { appData?: object }) => aVersion.appData as { version?: string } | undefined;
          const latestVersion = getAppData(event.latestVersion)?.version;

          const click$ = new Subject<void>();
          this.toaster.primary(
            this.translate.instant(latestVersion ? 'cas.versionAvailableValue' : 'cas.newVersion', {
              value: latestVersion,
            }),
            {
              delay: 100000000,
              size: 'lg',
              action: {
                click: () => click$.next(undefined),
                text: this.translate.instant('cas.reload').toUpperCase(),
              },
            },
          );
          return click$;
        }),
        first(),
        timeout(100000000),
        switchMap(() => this.updates.activateUpdate()),
      )
      .subscribe(updateActivatedFn);
  }
}
